/**
 * theme-prefs.js — simple per-user theme preferences (issue #252).
 *
 * What it controls:
 *   - **Theme**: light / dark / system (system = follow prefers-color-scheme)
 *   - **Accent**: one of 7 preset hexes (or null = use the theme's default)
 *   - **Density**: compact / medium / comfy
 *   - **Corner radius**: sharp / soft / rounded
 *
 * Storage (single localStorage key for atomic read/write):
 *   localStorage["luboo-prefs"] = '{"theme":"dark","accent":"#7C5CFF","density":"medium","radius":"soft"}'
 *
 * Two consumers:
 *   1. Settings UI calls `themePrefs.set(key, value)` on input changes;
 *      the prefs object is persisted and applied immediately.
 *   2. Page load (anywhere) imports this file via the bundle and calls
 *      `themePrefs.init()` which applies the saved prefs to <body>.
 *      Also wires a media-query listener so "system" mode flips when
 *      the OS theme changes.
 */

const STORAGE_KEY = 'luboo-prefs';

const DEFAULTS = {
  theme: 'system',           // 'light' | 'dark' | 'system'
  accent: null,              // hex string or null (= use the theme's --highlight-color)
  density: 'medium',         // 'compact' | 'medium' | 'comfy'
  radius: 'soft',            // 'sharp' | 'soft' | 'rounded'
};

/** Accent presets — these are the colors shown as swatches in the chooser.
 *  Drawn from the marketing palette + complementary hues so each works on
 *  both light and dark surfaces. */
export const ACCENT_PRESETS = [
  { hex: '#7C5CFF', label: 'Ember' },     // marketing brand
  { hex: '#6B8FA3', label: 'Sage' },      // marketing sage
  { hex: '#22C55E', label: 'Forest' },
  { hex: '#EAB308', label: 'Amber' },
  { hex: '#E17B4C', label: 'Sunset' },
  { hex: '#F472B6', label: 'Rose' },
  { hex: '#3498DB', label: 'Ocean' },
];

const RADIUS_PRESETS = {
  sharp: {
    '--radius-sm': '0px',
    '--radius-md': '0px',
    '--radius-lg': '0px',
    '--radius-xl': '0px',
    '--radius-pill': '0px',
  },
  soft: {
    '--radius-sm': '4px',
    '--radius-md': '8px',
    '--radius-lg': '12px',
    '--radius-xl': '20px',
    '--radius-pill': '9999px',
  },
  rounded: {
    '--radius-sm': '8px',
    '--radius-md': '14px',
    '--radius-lg': '20px',
    '--radius-xl': '28px',
    '--radius-pill': '9999px',
  },
};

const DENSITY_PRESETS = {
  compact: '0.75',
  medium: '1.0',
  comfy: '1.2',
};

/** Map our chooser theme key → the actual data-theme value on body.
 *  "light" → existing light theme. "dark" → lunaria (the marketing-aesthetic
 *  dark theme; polished as the brand default). */
const THEME_NAME = {
  light: 'light',
  dark: 'lunaria',
};

const STYLE_ID = 'user-prefs-overrides';

let _state = { ...DEFAULTS };
let _overlay = null;
let _mediaQuery = null;
let _initialized = false;

function effectivePrefs() {
  const merged = { ..._state };
  if (_overlay && typeof _overlay === 'object') {
    for (const key of ['theme', 'accent', 'density', 'radius']) {
      const value = _overlay[key];
      if (value != null && value !== '') merged[key] = value;
    }
    if (_overlay.light) merged.light = _overlay.light;
    if (_overlay.dark) merged.dark = _overlay.dark;
  }
  return merged;
}

function applyPaletteDecls(decls, prefs, resolved) {
  const palette = (resolved === 'light' ? prefs.light : prefs.dark) || {};
  if (palette.background) decls['--bg-color'] = palette.background;
  const accent = palette.accent || prefs.accent;
  if (accent) {
    decls['--highlight-color'] = accent;
    decls['--selection-bg'] = accent;
  }
  Object.entries(palette).forEach(([key, value]) => {
    if (key.startsWith('--') && value) decls[key] = value;
  });
}

// --- Storage -----------------------------------------------------------------

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULTS, ...parsed };
    }
    // One-shot migration from the pre-#252 single-string key. The old menu
    // had ten themes; only "light" maps cleanly onto the new chooser, so
    // every other named theme collapses to "dark" (lunaria). The legacy
    // key is deleted after we read it so we don't migrate twice.
    const legacy = localStorage.getItem('luboo-theme');
    if (legacy) {
      localStorage.removeItem('luboo-theme');
      const migrated = {
        ...DEFAULTS,
        theme: legacy === 'light' ? 'light' : 'dark',
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }
    return { ...DEFAULTS };
  } catch (_) {
    return { ...DEFAULTS };
  }
}

function writeStorage(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (_) {
    // ignore quota / disabled storage
  }
}

// --- DOM application ---------------------------------------------------------

/** Resolve `theme: 'system'` to the actual light/dark choice via prefers-color-scheme. */
function resolveSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToBody(prefs) {
  if (typeof document === 'undefined') return;

  // 1. Set body[data-theme] based on theme choice
  const resolved = prefs.theme === 'system' ? resolveSystemTheme() : prefs.theme;
  const dataTheme = THEME_NAME[resolved] || 'lunaria';
  document.body.setAttribute('data-theme', dataTheme);

  // 2. Compose override style block from accent + density + radius
  //    plus optional per-mode property palettes (Issue #691).
  const decls = {};
  applyPaletteDecls(decls, prefs, resolved);
  if (prefs.density && DENSITY_PRESETS[prefs.density]) {
    decls['--density-multiplier'] = DENSITY_PRESETS[prefs.density];
  }
  if (prefs.radius && RADIUS_PRESETS[prefs.radius]) {
    Object.assign(decls, RADIUS_PRESETS[prefs.radius]);
  }
  writeStyleBlock(decls);
}

/** Render a `<style>` tag at body scope with the given declarations.
 *  Selector list `body, body[data-theme]` ties on specificity with
 *  per-theme blocks in workspace.css, so source order resolves the tie
 *  in our favor. */
function writeStyleBlock(decls) {
  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  const lines = Object.entries(decls).map(([k, v]) => `  ${k}: ${v};`).join('\n');
  style.textContent = lines
    ? `body,\nbody[data-theme] {\n${lines}\n}`
    : '';
}

// --- Public API --------------------------------------------------------------

export const themePrefs = {
  /** Initialize on page load — call once at startup. */
  init() {
    if (_initialized) return;
    _initialized = true;
    _state = readStorage();
    applyToBody(effectivePrefs());

    // Listen for OS theme changes when in "system" mode
    if (typeof window !== 'undefined' && window.matchMedia) {
      _mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = () => {
        if (effectivePrefs().theme === 'system') applyToBody(effectivePrefs());
      };
      // Use addEventListener if available, addListener for older Safari
      if (_mediaQuery.addEventListener) {
        _mediaQuery.addEventListener('change', onChange);
      } else if (_mediaQuery.addListener) {
        _mediaQuery.addListener(onChange);
      }
    }

    // Cross-tab sync — when another tab updates prefs, refresh ours
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY) return;
        _state = readStorage();
        applyToBody(effectivePrefs());
      });
    }
  },

  /** Read the current prefs object (returns a copy). */
  get() {
    return { ..._state };
  },

  /** Set one preference and apply + persist. Valid keys: theme, accent, density, radius. */
  set(key, value) {
    if (!(key in DEFAULTS)) return;
    _state = { ..._state, [key]: value };
    writeStorage(_state);
    applyToBody(effectivePrefs());
  },

  /** Reset all prefs to defaults. */
  reset() {
    _state = { ...DEFAULTS };
    writeStorage(_state);
    applyToBody(effectivePrefs());
  },

  /**
   * Merge property branding over the user prefs while in a property
   * workspace (Issue #691). Pass null to return to the personal theme.
   */
  setOverlay(overlay) {
    _overlay = overlay && typeof overlay === 'object' ? overlay : null;
    applyToBody(effectivePrefs());
  },

  ACCENT_PRESETS,
};

// Expose on window AND auto-init on DOM ready so every page that loads
// any luboo bundle picks up the user's saved prefs without each app's
// kickoff needing to remember to call init().
if (typeof window !== 'undefined') {
  window.themePrefs = themePrefs;
  if (typeof document !== 'undefined') {
    if (document.readyState !== 'loading') {
      themePrefs.init();
    } else {
      document.addEventListener('DOMContentLoaded', () => themePrefs.init());
    }
  }
}
